
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';

import { SectionDefinition } from '@/features/locations';
import { useEntitySettings } from '@/features/shared/hooks/use-entity-settings';
import { useResponsiveValue } from '@/hooks/use-responsive-value';

interface CustomFieldsSectionProps {
  novelId: string;
  sections?: SectionDefinition[]; // Optional: if provided, won't fetch from Firestore
}

export function CustomFieldsSection({ sections: providedSections }: CustomFieldsSectionProps) {
  const { control, register, formState: { errors } } = useFormContext();
  const resolveResponsive = useResponsiveValue();
  
  // Use the canonical hook to get settings from denormalized novel data
  const { sections: hookSections, isLoading } = useEntitySettings('locations');
  
  const sections = providedSections || hookSections;

  if (isLoading && !providedSections) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!sections || sections.length === 0) {
    return null;
  }

  return (
    <>
      {sections.map((section) => (
        <Paper key={section.id} sx={{ p: 3, borderRadius: 2, mt: 4 }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {section.title}
          </Typography>

          <Grid container spacing={3}>
            {section.fields.map((field) => (
              <Grid key={field.id} size={resolveResponsive({ xs: 12, md: field.type === 'textarea' ? 12 : 6 })}>
                {field.type === 'select' ? (
                  <FormControl fullWidth variant="filled" size="small">
                    <InputLabel shrink>{field.label}</InputLabel>
                    <Controller
                      name={field.id}
                      control={control}
                      defaultValue=""
                      render={({ field: formField }) => (
                        <Select
                          {...formField}
                          label={field.label}
                          displayEmpty
                          error={!!errors[field.id]}
                          sx={{ borderRadius: 1.5, '&:before, &:after': { display: 'none' } }}
                        >
                          <MenuItem value="" disabled>
                            <em>{field.placeholder || 'Select...'}</em>
                          </MenuItem>
                          {field.options?.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                ) : (
                    <TextField
                      fullWidth
                      variant="filled"
                      size="small"
                      label={field.label}
                      placeholder={field.placeholder}
                      multiline={field.type === 'textarea'}
                      minRows={field.type === 'textarea' ? 4 : 1}
                      type={field.type === 'number' ? 'number' : 'text'}
                      {...register(field.id)}
                      error={!!errors[field.id]}
                      helperText={errors[field.id]?.message as string}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          borderRadius: 1.5,
                          px: 1.5,
                          py: field.type === 'textarea' ? 1.5 : 0,
                          '& textarea': field.type === 'textarea' ? { resize: 'vertical' } : {}
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                )}
              </Grid>
            ))}
          </Grid>
        </Paper>
      ))}
    </>
  );
}
